/* Copyright (c) 2020-present CloudIO Inc */
/* eslint-disable no-nested-ternary */

import {
  GridProp,
  ValueOf,
  constants,
  GridFlex,
  AnyManager,
} from 'componentregistry';
import { useStateXSelector } from '@cloudio-saas/statex';
import { Box, Slider, Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import { helpTextSelector } from 'designer/state';
import React, { memo } from 'react';
import clickOnEnterOrSpace from 'utils/clickOnEnterOrSpace';
import Icon from 'icons/Icon';
import { getDefaultValues } from './PropertyEditorUtils';
import { PropertyEditorProps } from './types';

const { gridSize } = constants;

const screenSize = {
  xs: 'Extra Small',
  sm: 'Small',
  md: 'Medium',
  lg: 'Large',
  xl: 'X Large',
  all: 'Flex',
} as const;

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    marginTop: 8,
  },
  inner: {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
    backgroundColor: theme.palette.background.default,
    borderRadius: 4,
    position: 'relative',
  },
  label: {
    flex: 0,
    minWidth: 80,
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
  },
  option: {
    fontSize: '0.8rem',
    alignSelf: 'center',
    border: '1px solid transparent',
    color: theme.palette.text.secondary,
    cursor: 'pointer',
    flex: 1,
    outline: 'none',
    overflow: 'hidden',
    padding: '4px 0',
    position: 'relative',
    textAlign: 'center',
    textOverflow: 'ellipsis',
    transition: 'color 0.2s',
    '& > svg': {
      height: '1em',
      width: '1em',
    },
    '&:not(:last-child)::after': {
      content: '""',
      position: 'absolute',
      top: 8,
      bottom: 8,
      right: 0,
      width: 1,
      backgroundColor: theme.palette.divider,
    },
    '&:not(:first-of-type)': {
      marginLeft: '-1px',
      borderTopLeftRadius: 0,
      borderBottomLeftRadius: 0,
    },
  },
  selected: {
    color: theme.palette.primary.main,
  },
  disabled: {
    color: theme.palette.action.disabled,
  },
  s: {
    backgroundColor: theme.palette.background.paper,
    position: 'absolute',
    top: 4,
    bottom: 4,
    borderRadius: 4,
    boxShadow: 'rgb(0 0 0 / 25%) 0px 1px 6px 0px',
    transition: 'left 0.2s cubic-bezier(0.2,0,0,1)',
  },
  slider: {
    flex: 2,
    height: '30px',
    overflow: 'visible',
    padding: '0 8px',
    zIndex: 1,
  },
  expander: {
    margin: '0 8px',
    outline: 'none',
    width: 14,
    '& > svg': {
      fill: theme.palette.primary.main,
    },
    cursor: 'pointer',
  },
  right: {
    textAlign: 'right',
    paddingRight: 8,
  },
}));

function Option({
  label,
  selected,
  onSelect,
  disabled,
}: {
  label: string;
  selected?: boolean;
  onSelect: () => void;
  disabled: boolean;
}) {
  const classes = useStyles();

  return (
    <div
      className={clsx({
        [classes.option]: true,
        [classes.selected]: selected,
        [classes.disabled]: disabled,
      })}
      onClick={disabled ? undefined : onSelect}
      onKeyDown={(e) => !disabled && clickOnEnterOrSpace(e, onSelect)}
      role="button"
      tabIndex={-1}
    >
      {label}
    </div>
  );
}

const marks = [...Array(12)].map((_, i) => ({ value: i + 1 }));

function FlexRow({
  appUid,
  activeItemId,
  label,
  pageId,
  property,
  mgr,
  value,
  onChange,
  expanded,
}: {
  appUid: string;
  label: keyof GridFlex;
  activeItemId: string;
  pageId: string;
  property: string;
  mgr: AnyManager;
  value: ValueOf<typeof gridSize> | undefined;
  onChange: (val?: ValueOf<typeof gridSize>, expand?: boolean) => void;
  expanded: boolean;
}) {
  const classes = useStyles();
  const helpText = useStateXSelector(helpTextSelector, {
    params: { appUid, pageId, itemId: activeItemId, property, type: mgr.type },
  });

  const left =
    value === 'auto'
      ? 4
      : value === true
      ? '75%'
      : value === false
      ? 'calc(25% - 24px)'
      : undefined;

  const hasValue = value !== undefined;
  const disabled = label === 'all' ? expanded : !hasValue;

  function onClick() {
    if (label !== 'all') {
      onChange(hasValue ? undefined : false);
    }
    if (label === 'all') {
      onChange(value, true);
    }
  }

  return (
    <div data-testid={property} className={classes.root}>
      <div
        className={clsx(classes.label, label !== 'all' ? classes.right : null)}
        data-tip-at="top"
        data-tip={helpText}
      >
        {screenSize[label]}
      </div>
      <div
        className={classes.inner}
        data-tip={
          label !== 'all' && !hasValue
            ? `Check the box at the end to update ${screenSize[label]}'s flex value`
            : undefined
        }
      >
        {left !== undefined ? (
          <Box
            sx={{
              width: 'calc(25% - 36px)',
              left,
            }}
            className={classes.s}
          />
        ) : null}
        <Option
          label="Fit"
          disabled={disabled}
          onSelect={() => {
            onChange('auto');
          }}
          selected={value === 'auto'}
        />
        <Option
          label="None"
          disabled={disabled}
          onSelect={() => {
            onChange(false);
          }}
          selected={value === false}
        />
        <div className={clsx(classes.option, classes.slider)}>
          <Slider
            disabled={disabled}
            valueLabelDisplay="auto"
            valueLabelFormat={(v: number) =>
              v === -5 ? 'Fit' : v === 15 ? 'Fill' : v === -2 ? 'None' : v
            }
            step={null}
            min={-5}
            max={15}
            marks={marks}
            onChange={(_: Event, val: number | number[]) => {
              if (typeof val === 'number' && val > 0 && val < 13) {
                // @ts-ignore val is 1-12
                onChange(val);
              } else if (val === -5) {
                onChange('auto');
              } else if (val === -2) {
                onChange(false);
              } else if (val === 15) {
                onChange(true);
              }
            }}
            value={
              typeof value === 'number'
                ? value
                : value === true
                ? 15
                : value === false || value === undefined
                ? -2
                : -5
            }
          />
        </div>
        <Option
          label="Fill"
          disabled={disabled}
          onSelect={() => {
            onChange(true);
          }}
          selected={value === true}
        />
        <div
          onKeyDown={(e) => clickOnEnterOrSpace(e, onClick)}
          role="button"
          tabIndex={-1}
          data-tip={
            // eslint-disable-next-line no-nested-ternary
            label !== 'all'
              ? hasValue
                ? `Clear ${screenSize[label]} flex`
                : `Set ${screenSize[label]} flex`
              : expanded
              ? 'Remove independent flex'
              : 'Set independent flex'
          }
          className={clsx(
            classes.expander,
            label !== 'all' && hasValue ? classes.selected : null,
            label === 'all' && expanded ? classes.selected : null,
          )}
          onClick={onClick}
        >
          {label === 'all' ? (
            <Icon color="primary" iconName={expanded ? 'compress' : 'expand'} />
          ) : null}
          {label !== 'all' && !hasValue ? (
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
              <path d="M400 32H48C21.5 32 0 53.5 0 80v352c0 26.5 21.5 48 48 48h352c26.5 0 48-21.5 48-48V80c0-26.5-21.5-48-48-48zm16 400c0 8.8-7.2 16-16 16H48c-8.8 0-16-7.2-16-16V80c0-8.8 7.2-16 16-16h352c8.8 0 16 7.2 16 16v352z" />
            </svg>
          ) : null}
          {label !== 'all' && hasValue ? (
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
              <path d="M400 32H48C21.49 32 0 53.49 0 80v352c0 26.51 21.49 48 48 48h352c26.51 0 48-21.49 48-48V80c0-26.51-21.49-48-48-48zm0 32c8.823 0 16 7.178 16 16v352c0 8.822-7.177 16-16 16H48c-8.822 0-16-7.178-16-16V80c0-8.822 7.178-16 16-16h352m-34.301 98.293l-8.451-8.52c-4.667-4.705-12.265-4.736-16.97-.068l-163.441 162.13-68.976-69.533c-4.667-4.705-12.265-4.736-16.97-.068l-8.52 8.451c-4.705 4.667-4.736 12.265-.068 16.97l85.878 86.572c4.667 4.705 12.265 4.736 16.97.068l180.48-179.032c4.704-4.667 4.735-12.265.068-16.97z" />
            </svg>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function FlexProperty({
  appUid,
  activeItemId,
  pageId,
  property,
  mgr,
  value = getDefaultValues<GridFlex>(mgr, property),
  onChange: setValue,
}: PropertyEditorProps<GridProp, GridFlex>) {
  const expanded = !!(
    value.xs !== undefined ||
    value.sm !== undefined ||
    value.md !== undefined ||
    value.lg !== undefined ||
    value.xl !== undefined
  );
  const sizes = ['all', 'xl', 'lg', 'md', 'sm', 'xs'] as const;

  return (
    <>
      {expanded ? (
        sizes.map((size: keyof GridFlex) => (
          <FlexRow
            appUid={appUid}
            key={size}
            activeItemId={activeItemId}
            pageId={pageId}
            label={size}
            property={property}
            mgr={mgr}
            value={value[size]}
            expanded
            onChange={(m, expand) => {
              if (expand) {
                setValue(property, { all: value.xs });
              } else if (value && m === undefined) {
                const val = { ...value };
                delete val[size];
                if (Object.keys(val).length) {
                  setValue(property, val);
                } else {
                  setValue(property, undefined);
                }
              } else {
                setValue(property, { ...value, [size]: m });
              }
            }}
          />
        ))
      ) : (
        <FlexRow
          appUid={appUid}
          activeItemId={activeItemId}
          pageId={pageId}
          label="all"
          property={property}
          mgr={mgr}
          value={value.all}
          expanded={false}
          onChange={(v, expand) => {
            setValue(
              property,
              expand
                ? {
                    xs: v,
                  }
                : { all: v },
            );
          }}
        />
      )}
    </>
  );
}

export default memo(FlexProperty);
